import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  billing as billingApi,
  discover as discoverApi,
  documents as docsApi,
  type DiscoverDoc,
  type DocumentSummary,
} from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { shouldShowOnboarding } from "@/pages/Onboarding";
import { AppFooter } from "@/components/AppFooter";
import { TryItPanel } from "@/components/TryItPanel";
import { UpgradePrompt } from "@/components/UpgradePrompt";

const STATUS_COLOR: Record<DocumentSummary["global_status"], string> = {
  outlining: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200",
  drafting: "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-200",
  awaiting_humans:
    "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
  published:
    "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200",
};

/** Visibility filter for "Your documents". */
type VisFilter = "all" | "private" | "public";

export function Dashboard() {
  // 14 private / 5 public on a real account today, and comparisons add one
  // per run — the list outgrew "just scroll" quickly, and a private doc looked
  // identical to a published one.
  const [docQuery, setDocQuery] = useState("");
  const [visFilter, setVisFilter] = useState<VisFilter>("all");
  const [showCompare, setShowCompare] = useState(false);
  const { state, signOut } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [showNew, setShowNew] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["documents"],
    queryFn: () => docsApi.list(),
  });

  const discoverQuery = useQuery({
    queryKey: ["discover"],
    queryFn: () => discoverApi.list({ limit: 12 }),
  });

  // Lightweight billing/usage probe — drives the dashboard quota banner.
  // Refetches every 5 min to keep usage roughly fresh without spamming.
  const billingQuery = useQuery({
    queryKey: ["billing_me"],
    queryFn: () => billingApi.me(),
    staleTime: 5 * 60 * 1000,
  });

  // First-run redirect: if the user has never been onboarded AND has zero
  // documents, send them to the wizard. Once they finish (or skip), the flag
  // is set and they land here on subsequent visits.
  useEffect(() => {
    if (!data) return;
    if (data.documents.length === 0 && shouldShowOnboarding()) {
      navigate("/onboarding", { replace: true });
    }
  }, [data, navigate]);

  const createMut = useMutation({
    mutationFn: (input: { title: string; visibility?: "public" | "private" }) =>
      docsApi.create(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["documents"] });
      setShowNew(false);
    },
  });

  if (state.status !== "authenticated") return null;
  const { user } = state;

  const visibleDocs = (data?.documents ?? []).filter(
    (d) =>
      (visFilter === "all" ||
        (visFilter === "private"
          ? d.visibility === "private"
          : d.visibility !== "private")) &&
      (docQuery.trim() === "" ||
        d.title.toLowerCase().includes(docQuery.trim().toLowerCase())),
  );

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-zinc-950">
      {/* Top bar — matches the anonymous Landing chrome so users see consistent
          framing when they sign in. */}
      <header className="border-b border-zinc-200 dark:border-zinc-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 font-semibold tracking-tight text-lg"
          >
            <img
              src="/coauthor-mark.svg"
              alt=""
              className="w-6 h-6"
              aria-hidden="true"
            />
            Coauthor
          </Link>
          <nav className="flex items-center gap-3 sm:gap-4 text-sm text-zinc-500 flex-wrap">
            <Link
              to="/getting-started"
              className="hover:text-zinc-900 dark:hover:text-zinc-100 font-medium"
            >
              Getting started
            </Link>
            <Link to="/tokens" className="hover:text-zinc-900 dark:hover:text-zinc-100">
              Agent tokens
            </Link>
            <Link to="/billing" className="hover:text-zinc-900 dark:hover:text-zinc-100">
              Billing
            </Link>
            <Link to="/settings" className="hover:text-zinc-900 dark:hover:text-zinc-100">
              Settings
            </Link>
            <span className="truncate max-w-[10rem] text-zinc-700 dark:text-zinc-300" title={user.email}>
              {user.display_name ?? user.email}
            </span>
            <button
              type="button"
              onClick={() => void signOut()}
              className="hover:text-zinc-900 dark:hover:text-zinc-100"
            >
              Sign out
            </button>
          </nav>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10">

      <QuotaBanner billing={billingQuery.data} />

      {/* Your documents lead. They used to sit third, under Discover and a
          live activity feed — which put other people's work and a mostly-idle
          ticker above the thing the user came for. The live feed is gone
          entirely: at this population it renders as an empty room, and an
          empty room is worse than no room. */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-lg font-medium">Your documents</h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              setShowCompare((v) => !v);
              setShowNew(false);
            }}
            className="text-sm rounded-md border border-zinc-300 dark:border-zinc-700 px-3 py-1.5 hover:bg-zinc-50 dark:hover:bg-zinc-900"
          >
            {showCompare ? "Cancel" : "Compare models"}
          </button>
          <button
            type="button"
            onClick={() => {
              setShowNew((v) => !v);
              setShowCompare(false);
            }}
            className="text-sm rounded-md bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 text-white px-3 py-1.5 hover:opacity-90"
          >
            {showNew ? "Cancel" : "New document"}
          </button>
        </div>
      </div>

      {/* The compare panel was previously reachable only from the zero-document
          empty state, so it vanished permanently after the first document —
          even though comparing models is a thing you want repeatedly. */}
      {showCompare && (
        <div className="mb-6">
          <TryItPanel />
        </div>
      )}

      {showNew && (
        <NewDocumentForm
          busy={createMut.isPending}
          error={
            createMut.error instanceof ApiError && createMut.error.status === 402
              ? null
              : createMut.error instanceof ApiError
              ? ((createMut.error.body as { error?: string } | null)?.error ??
                  `${createMut.error.status}`)
              : createMut.error
                ? String(createMut.error)
                : null
          }
          onSubmit={(title, visibility) => createMut.mutate({ title, visibility })}
          planLimitError={
            createMut.error instanceof ApiError && createMut.error.status === 402
              ? createMut.error
              : null
          }
          onRetryPublic={(title) =>
            createMut.mutate({ title, visibility: "public" })
          }
        />
      )}

      {isLoading && <div className="text-sm text-zinc-500">Loading…</div>}
      {error && (
        <div className="text-sm text-red-600 dark:text-red-400">
          Failed to load: {String(error)}
        </div>
      )}

      {data && data.documents.length === 0 && !showNew && <TryItPanel />}

      {data && data.documents.length > 0 && (
        <DocFilters
          total={data.documents.length}
          query={docQuery}
          onQuery={setDocQuery}
          vis={visFilter}
          onVis={setVisFilter}
          counts={{
            private: data.documents.filter((d) => d.visibility === "private").length,
            public: data.documents.filter((d) => d.visibility !== "private").length,
          }}
        />
      )}

      {data && data.documents.length > 0 && visibleDocs.length === 0 && (
        <div className="rounded-md border border-dashed border-zinc-300 dark:border-zinc-700 p-6 text-center text-sm text-zinc-500">
          No documents match{docQuery.trim() ? ` “${docQuery.trim()}”` : ""}
          {visFilter !== "all" ? ` in ${visFilter}` : ""}.
        </div>
      )}

      {data && visibleDocs.length > 0 && (
        <ul className="divide-y divide-zinc-200 dark:divide-zinc-800 rounded-md border border-zinc-200 dark:border-zinc-800">
          {visibleDocs
            .map((doc) => (
            <li key={doc.id}>
              <Link
                to={`/d/${doc.id}`}
                className="flex items-center justify-between px-4 py-3 hover:bg-zinc-50 dark:hover:bg-zinc-900 transition"
              >
                <div className="min-w-0 mr-4">
                  <div className="font-medium truncate">{doc.title}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    {doc.member_count} member{doc.member_count === 1 ? "" : "s"} ·{" "}
                    {doc.updated_at
                      ? new Date(doc.updated_at).toLocaleString()
                      : "unknown"}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {doc.visibility === "private" && (
                    <span
                      className="text-xs px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400"
                      title="Only members can see this document"
                    >
                      private
                    </span>
                  )}
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLOR[doc.global_status]}`}
                  >
                    {doc.global_status.replace("_", " ")}
                  </span>
                  <span className="text-xs text-zinc-500 uppercase tracking-wide">
                    {doc.role}
                  </span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
        <div className="mt-12 pt-8 border-t border-zinc-200 dark:border-zinc-800">
          <DiscoverSection
            loading={discoverQuery.isLoading}
            docs={discoverQuery.data?.documents ?? []}
          />
        </div>
      </div>
      <AppFooter />
    </div>
  );
}

/**
 * QuotaBanner — shown inline on the dashboard when a free user crosses 80%
 * of their monthly quota. Hidden for Pro / heavy / internal tiers and for
 * usage below the threshold so it doesn't nag users who aren't close to a
 * limit.
 */
/**
 * Search + visibility filter for "Your documents".
 *
 * Two things the plain list couldn't express: which documents are published,
 * and where a given one is. A private document and a public one rendered
 * identically, which matters now that publishing is a deliberate act — you
 * could not tell at a glance what you had shared.
 */
function DocFilters({
  total,
  query,
  onQuery,
  vis,
  onVis,
  counts,
}: {
  total: number;
  query: string;
  onQuery: (v: string) => void;
  vis: VisFilter;
  onVis: (v: VisFilter) => void;
  counts: { private: number; public: number };
}) {
  const tabs: { key: VisFilter; label: string }[] = [
    { key: "all", label: `All ${total}` },
    { key: "private", label: `Private ${counts.private}` },
    { key: "public", label: `Public ${counts.public}` },
  ];
  return (
    <div className="flex flex-wrap items-center gap-2 mb-3">
      <div className="flex rounded-md border border-zinc-200 dark:border-zinc-800 overflow-hidden">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => onVis(t.key)}
            className={`text-xs px-3 py-1.5 transition ${
              vis === t.key
                ? "bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900"
                : "hover:bg-zinc-50 dark:hover:bg-zinc-900 text-zinc-600 dark:text-zinc-400"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <input
        type="search"
        value={query}
        onChange={(e) => onQuery(e.target.value)}
        placeholder="Filter by title…"
        className="flex-1 min-w-[10rem] rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-1.5 text-sm"
      />
    </div>
  );
}


function QuotaBanner({
  billing,
}: {
  billing:
    | {
        tier: string;
        usage_state: import("@/lib/api").UsageState;
        billing_configured: boolean;
      }
    | undefined;
}) {
  if (!billing) return null;
  if (billing.tier !== "free") return null;
  if (!billing.billing_configured) return null;
  // Banner only shows for the two states that need user attention. Anything
  // below "approaching_limit" is fine and we don't nag.
  if (
    billing.usage_state !== "approaching_limit" &&
    billing.usage_state !== "at_limit"
  ) {
    return null;
  }

  const overLimit = billing.usage_state === "at_limit";
  const cls = overLimit
    ? "border-red-300 bg-red-50 dark:bg-red-950/30 dark:border-red-700"
    : "border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700";
  // Qualitative copy only — no numbers exposed. Per the opacity policy,
  // raw monthly_used / monthly_limit are infrastructure, not contract.
  // Says "AI reviews", not "tool calls": the quota only applies to tools that
  // call a model. Collaboration — drafting, commenting, claiming, membership —
  // is free and unlimited, and a banner implying otherwise would be alarming
  // and wrong.
  const headline = overLimit
    ? "You've used your AI reviews for this month. Collaboration is unaffected."
    : "You're approaching your monthly limit for AI reviews.";

  return (
    <div className={`mb-6 rounded-md border p-3 text-sm flex items-center justify-between gap-3 ${cls}`}>
      <span>{headline}</span>
      <Link
        to="/billing"
        className="rounded-md bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 text-white px-3 py-1.5 text-xs font-medium hover:opacity-90 whitespace-nowrap"
      >
        Upgrade to Pro →
      </Link>
    </div>
  );
}

function DiscoverSection({
  loading,
  docs,
}: {
  loading: boolean;
  docs: DiscoverDoc[];
}) {
  return (
    <section>
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-lg font-medium">Discover</h2>
        <div className="flex items-center gap-3">
          <Link
            to="/questions"
            className="text-xs text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 underline decoration-zinc-300 dark:decoration-zinc-700 underline-offset-2"
          >
            View Q&A feed →
          </Link>
          <span className="text-xs text-zinc-500 hidden sm:inline">
            Public docs anyone can read or contribute to
          </span>
        </div>
      </div>
      {loading ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : docs.length === 0 ? (
        <div className="rounded-md border border-dashed border-zinc-300 dark:border-zinc-700 p-6 text-center text-sm text-zinc-500">
          No public documents yet. Be the first — create one above.
        </div>
      ) : (
        <ul className="grid sm:grid-cols-2 gap-3">
          {docs.map((d) => (
            <li key={d.id}>
              <Link
                to={`/d/${d.id}`}
                className="block rounded-md border border-zinc-200 dark:border-zinc-800 p-3 hover:border-zinc-400 dark:hover:border-zinc-600 transition"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="font-medium truncate">{d.title}</div>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${STATUS_COLOR[d.global_status] ?? ""}`}
                  >
                    {d.global_status.replace("_", " ")}
                  </span>
                </div>
                <div className="text-xs text-zinc-500 mt-1.5 truncate">
                  by {d.owner.display_name} · {d.member_count} member
                  {d.member_count === 1 ? "" : "s"}
                  {d.updated_at && ` · ${new Date(d.updated_at).toLocaleDateString()}`}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function NewDocumentForm(props: {
  busy: boolean;
  error: string | null;
  onSubmit: (title: string, visibility: "public" | "private") => void;
  planLimitError?: ApiError | null;
  onRetryPublic?: (title: string) => void;
}) {
  const [title, setTitle] = useState("");
  // Public is the default: it's what fills the catalogue, and it's the one
  // that's unlimited. The private option is visible rather than hidden —
  // a paid feature nobody can see doesn't convert.
  const [visibility, setVisibility] = useState<"public" | "private">("public");
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    props.onSubmit(title.trim(), visibility);
  };
  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-md border border-zinc-200 dark:border-zinc-800 p-4 mb-6"
    >
      <label className="block">
        <span className="text-sm font-medium">Title</span>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          autoFocus
          maxLength={200}
          placeholder="Joint position paper on…"
          className="mt-1 block w-full rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-2"
        />
      </label>

      <div className="mt-3 flex items-center gap-3 text-sm">
        <span className="font-medium">Visibility</span>
        <div className="flex rounded-md border border-zinc-200 dark:border-zinc-800 overflow-hidden">
          {(["public", "private"] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setVisibility(v)}
              className={`px-3 py-1 text-xs transition ${
                visibility === v
                  ? "bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900"
                  : "hover:bg-zinc-50 dark:hover:bg-zinc-900 text-zinc-600 dark:text-zinc-400"
              }`}
            >
              {v}
            </button>
          ))}
        </div>
        <span className="text-xs text-zinc-500">
          {visibility === "public"
            ? "Anyone can read it; unlimited."
            : "Only members can read it."}
        </span>
      </div>

      {/* 402 = the private-document limit. Offer publishing first, since it
          costs nothing and keeps the user moving. */}
      {props.planLimitError && (
        <div className="mt-3">
          <UpgradePrompt
            error={props.planLimitError}
            publicLabel="Make it public instead"
            onMakePublic={() => {
              if (title.trim() && props.onRetryPublic) {
                setVisibility("public");
                props.onRetryPublic(title.trim());
              }
            }}
          />
        </div>
      )}

      {props.error && (
        <div className="text-sm text-red-600 dark:text-red-400 mt-2">{props.error}</div>
      )}
      <div className="mt-3 flex gap-2">
        <button
          type="submit"
          disabled={props.busy || !title.trim()}
          className="rounded-md bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 text-white px-3 py-1.5 text-sm disabled:opacity-50"
        >
          {props.busy ? "Creating…" : "Create"}
        </button>
      </div>
    </form>
  );
}
